export const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pad = (n) => String(n).padStart(2, "0");
export const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
};

export const shortDate = (s) => {
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : s || "";
};

export const monthYear = (s) => {
  const m = String(s || "").match(/^(\d{4})-(\d{2})/);
  return m ? `${+m[2]}/${m[1]}` : s || "";
};

// Facilities whose equipment prints on a dedicated Equipment page (page 1 stays blank with a
// "SEE ATTACHED" note). Every other job keeps equipment inline on the main invoice.
const EQUIP_PAGE_KEYS = ["golden acres", "jackson health", "legacy midtown", "pleasant springs", "lake highlands", "villages of dallas", "kirkwood manor"];
export const isEquipmentPageJob = (name) => {
  const n = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return !!n && EQUIP_PAGE_KEYS.some((k) => n.includes(k));
};

// Short "12 scales · 6 lifts" summary for at-a-glance counts. Ignores blanks and "SEE ATTACHED".
export const equipSummary = (inv) => {
  const real = (arr) => (arr || []).filter((x) => String(x || "").trim() && !/see attached/i.test(String(x))).length;
  const s = real(inv.scales), o = real(inv.other_equipment), l = real(inv.lifts);
  const parts = [];
  if (s) parts.push(`${s} scale${s === 1 ? "" : "s"}`);
  if (o) parts.push(`${o} other`);
  if (l) parts.push(`${l} lift${l === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

// Total count of real equipment items (scales + other + lifts), ignoring blanks and "SEE ATTACHED".
export const equipCount = (inv) => {
  const real = (arr) => (arr || []).filter((x) => String(x || "").trim() && !/see attached/i.test(String(x))).length;
  return real(inv.scales) + real(inv.other_equipment) + real(inv.lifts);
};


export function calcTotals(inv) {
  const sub = Number(inv.subtotal) || 0;
  const tax = Math.round((sub * (Number(inv.tax_rate) || 0)) / 100 * 100 + 1e-7) / 100;
  const total = sub + tax;
  const o = inv.plus_total;
  // +Total is always the manually entered amount (no auto-calc). Blank stays blank (0).
  const grand = o !== null && o !== undefined && o !== "" ? (Number(o) || 0) : 0;
  return { tax, total, grand_total: grand };
}

export const localDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export const PAGE2 = ["Special Notes", "County code", "County Tax", "Cty code", "Cty tax", "SpecTax 1", "Sp 1 code", "Sp 1 amt",
  "SpecTax 2", "Sp 2 code", "Sp 2 amt", "SpecTax 3", "Sp 3 code", "Sp 3 amt"];
const pnorm = (x) => String(x).toLowerCase().replace(/[^a-z0-9]/g, "");
const PAGE2_KEYS = new Set(PAGE2.map(pnorm));
const PAGE2_NAME = Object.fromEntries(PAGE2.map((k) => [pnorm(k), k]));
export const hasPage2 = (inv) => Object.values(inv.page2 || {}).some((v) => String(v ?? "").trim());

export const blankInvoice = (settings) => ({
  page2: {},
  number: "",
  service_date: today(),
  tech: "",
  customer: { name: "", address: "", city_state_zip: "", phone: "", administrator: "", contact: "", corporate_info: "" },
  months: Array(12).fill(false),
  frequency: "",
  service_performed: settings.service_text || "",
  sticker_nos: "",
  scales: [""],
  other_equipment: [],
  lifts: [""],
  subtotal: 0,
  tax_rate: settings.tax_rate ?? 0,
  parts: 0,
  plus_total: null,
  terms: settings.terms || "",
  status: "unpaid",
  date_paid: null,
});

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const NAME_PRIORITY = [/facility/, /company/, /^(name|fullname|customername|clientname|accountname)$/, /name/, /customer|account|client/];
const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
const truthy = (v) => v === true || /^(x|y|yes|true|1|✓)$/i.test(String(v ?? "").trim());
const toDate = (v) => {
  const s = String(v ?? "");
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  const y = m[3].length === 2 ? (+m[3] > 68 ? 1900 : 2000) + +m[3] : +m[3];
  return `${y}-${pad(m[1])}-${pad(m[2])} 00:00:00`;
};

// Maps a database record (e.g. one row of a Works .wdb) onto the service invoice form by field names.
export function prefillFromRecord(record, columns, dbId, table, settings) {
  const inv = blankInvoice(settings);
  const lists = { scales: [], other_equipment: [], lifts: [] };
  const city = [];
  const got = {};
  const once = (key, fn) => { if (!got[key]) { got[key] = true; fn(); } };
  const splitLines = (s) => s.split(/\s+(?=(?:\d{1,2}|[A-Za-z])\)\s)/).map((x) => x.replace(/^(?:\d{1,2}|[A-Za-z])\)\s*/, "").trim()).filter(Boolean);
  for (const c of columns) {
    const n = norm(c.name);
    const v = record.data[c.name];
    if (v === null || v === undefined || v === "") continue;
    const s = String(v).trim();
    const month = MONTHS.findIndex((m) => n === m || n.startsWith(m) && n.length <= 9);
    if (/^(m|month)?(\d{1,2})$/.test(n) && +n.match(/\d+/)[0] >= 1 && +n.match(/\d+/)[0] <= 12) inv.months[+n.match(/\d+/)[0] - 1] = truthy(v);
    else if (month >= 0) inv.months[month] = truthy(v);
    else if (/sig$|signature/.test(n)) continue;
    else if (/invoice|^inv(no|num)?$/.test(n)) inv.number = s;
    else if (/sticker/.test(n)) inv.sticker_nos = s;
    else if (/lift/.test(n)) lists.lifts.push(...splitLines(s));
    else if (/scale/.test(n)) lists.scales.push(...splitLines(s));
    else if (/other|equip/.test(n)) lists.other_equipment.push(...splitLines(s));
    else if (/freq/.test(n)) inv.frequency = s;
    else if (/^(datepd|datepaid|paiddate|paidon|paid|pddate)$/.test(n) && toDate(v)) { inv.date_paid = toDate(v); inv.status = "paid"; }
    else if (/tech/.test(n)) once("tech", () => { inv.tech = s; });
    else if (/servicedate|svcdate|lastservice/.test(n) && toDate(v)) { inv.service_date = toDate(v); got.date = true; }
    else if (/^date$/.test(n) && toDate(v)) once("date", () => { inv.service_date = toDate(v); });
    else if (PAGE2_KEYS.has(n)) inv.page2[PAGE2_NAME[n]] = s;
    else if (/special/.test(n)) continue;
    else if (/service|performed|work|notes?|comment/.test(n)) once("service", () => { inv.service_performed = s; });
    else if (/admin/.test(n)) inv.customer.administrator = s;
    else if (/contact|attn/.test(n) && !/title|id$/.test(n)) inv.customer.contact ||= s;
    else if (/corp|parent|chain|owner/.test(n)) inv.customer.corporate_info = s;
    else if (/phone|tel/.test(n) && !/fax|tollfree/.test(n)) inv.customer.phone ||= s;
    else if (/^(city|state|st|zip|zipcode|postal|postalcode|csz|citystatezip)$/.test(n)) city.push(s);
    else if (/address|street|addr/.test(n)) inv.customer.address = [inv.customer.address, s].filter(Boolean).join(", ");
    else if (/subtotal|amount|charge|price|fee/.test(n) && !isNaN(parseFloat(s.replace(/[$,]/g, "")))) inv.subtotal = parseFloat(s.replace(/[$,]/g, ""));
    else if (/^parts$/.test(n) && !isNaN(parseFloat(s.replace(/[$,]/g, "")))) inv.parts = parseFloat(s.replace(/[$,]/g, ""));
  }
  const nameCol = NAME_PRIORITY.map((re) => columns.find((c) => c.type === "text" && re.test(norm(c.name)) && !/id$|title/.test(norm(c.name))
    && record.data[c.name])).find(Boolean);
  inv.customer.name = String(record.data[(nameCol || columns.find((c) => c.type === "text"))?.name] ?? "");
  if (city.length) inv.customer.city_state_zip = city.length === 3 ? `${city[0]}, ${city[1]}  ${city[2]}` : city.join(" ");
  for (const k of Object.keys(lists)) if (lists[k].length) inv[k] = lists[k];
  // Sales tax rule: if this record has the tax-jurisdiction fields (County Tax, Cty tax, SpecTax 1)
  // and they are ALL empty, charge no sales tax for this customer.
  const TAX_FIELDS = ["countytax", "ctytax", "spectax1"];
  const taxCols = columns.filter((c) => TAX_FIELDS.includes(norm(c.name)));
  if (taxCols.length && taxCols.every((c) => {
    const tv = record.data[c.name];
    return tv === null || tv === undefined || String(tv).trim() === "";
  })) inv.tax_rate = 0;
  return { ...inv, source: { db_id: dbId, table, record_id: record.id } };
}

const REPEAT_KEYS = ["page2", "tech", "customer", "months", "frequency", "service_performed", "sticker_nos", "scales", "other_equipment", "lifts", "subtotal", "tax_rate", "terms", "source"];

// Copies the facility, equipment and schedule from an earlier invoice; new date, number and parts.
export function fromPrevious(prev, base) {
  const out = { ...base };
  for (const k of REPEAT_KEYS) if (prev[k] !== undefined && prev[k] !== null) out[k] = JSON.parse(JSON.stringify(prev[k]));
  return out;
}

export async function fetchAllRecords(api, path) {
  const all = [];
  for (let page = 1; ; page += 1) {
    const { data } = await api.get(`${path}/records`, { params: { page, page_size: 200 } });
    all.push(...data.records);
    if (all.length >= data.total || !data.records.length) return all;
  }
}

const str = (v) => String(v ?? "").toLowerCase();
export const SORTS = {
  service_desc: ["Service date: newest", (a, b) => str(b.service_date).localeCompare(str(a.service_date))],
  service_asc: ["Service date: oldest", (a, b) => str(a.service_date).localeCompare(str(b.service_date))],
  number_asc: ["Invoice #: A to Z", (a, b) => str(a.number).localeCompare(str(b.number), undefined, { numeric: true })],
  number_desc: ["Invoice #: Z to A", (a, b) => str(b.number).localeCompare(str(a.number), undefined, { numeric: true })],
  facility: ["Facility: A to Z", (a, b) => str(a.customer?.name).localeCompare(str(b.customer?.name))],
  route: ["Route", (a, b) => (!a.route - !b.route) || str(a.route).localeCompare(str(b.route), undefined, { numeric: true }) || str(a.number).localeCompare(str(b.number))],
  tech: ["Tech", (a, b) => (!a.tech - !b.tech) || str(a.tech).localeCompare(str(b.tech))],
  status: ["Status", (a, b) => str(a.status).localeCompare(str(b.status))],
  paid_desc: ["Date Pd: newest", (a, b) => (!a.date_paid - !b.date_paid) || str(b.date_paid).localeCompare(str(a.date_paid))],
  paid_asc: ["Date Pd: oldest", (a, b) => (!a.date_paid - !b.date_paid) || str(a.date_paid).localeCompare(str(b.date_paid))],
  total_desc: ["+Total: highest", (a, b) => (b.grand_total || 0) - (a.grand_total || 0)],
  total_asc: ["+Total: lowest", (a, b) => (a.grand_total || 0) - (b.grand_total || 0)],
  equip_desc: ["Equipment: most", (a, b) => equipCount(b) - equipCount(a)],
  equip_asc: ["Equipment: fewest", (a, b) => equipCount(a) - equipCount(b)],
};

export const sortInvoices = (list) => [...list].sort((SORTS[localStorage.getItem("invoice-sort")] || SORTS.service_desc)[1]);
